import { useEffect, useRef, useState } from 'react';

const toRadians = (deg) => (deg * Math.PI) / 180;

// Approximate haversine distance between two [lat, lng] points in meters.
const distanceMeters = (from, to) => {
	if (!from || !to) return Infinity;
	const [lat1, lng1] = from;
	const [lat2, lng2] = to;
	if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return Infinity;
	const R = 6371e3;
	const phi1 = toRadians(lat1);
	const phi2 = toRadians(lat2);
	const dPhi = toRadians(lat2 - lat1);
	const dLambda = toRadians(lng2 - lng1);
	const a =
		Math.sin(dPhi / 2) ** 2 +
		Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const easeInOutCubic = (t) =>
	t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;



/**
 * Smoothly animate a marker position from the previous point to the target.
 * @param {[number, number]|null} targetLatLng
 * @param {{
 *  durationMs?: number,
 *  snapDistanceMeters?: number,
 * }} options
 * @returns {[number, number]|null}
 */
export const useSmoothMarkerPosition = (
	targetLatLng,
	{
		durationMs = 1000, // fixed animation duration
		snapDistanceMeters = 100, // snap if more than 200m away
	} = {}
) => {
	const targetLat = Array.isArray(targetLatLng) ? targetLatLng[0] : null;
	const targetLng = Array.isArray(targetLatLng) ? targetLatLng[1] : null;
	const [renderPos, setRenderPos] = useState(
		Number.isFinite(targetLat) && Number.isFinite(targetLng) ? [targetLat, targetLng] : null
	);
	const fromRef = useRef(
		Number.isFinite(targetLat) && Number.isFinite(targetLng) ? [targetLat, targetLng] : null
	);
	const currentPosRef = useRef(
		Number.isFinite(targetLat) && Number.isFinite(targetLng) ? [targetLat, targetLng] : null
	);
	const rafRef = useRef(null);

	useEffect(() => {
		const target =
			Number.isFinite(targetLat) && Number.isFinite(targetLng)
				? [targetLat, targetLng]
				: null;
		if (!target) return;

		const from =
			currentPosRef.current ||
			fromRef.current ||
			(Array.isArray(renderPos) ? renderPos : null);
		const shouldSkip = !from || durationMs <= 0;

		if (shouldSkip) {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			fromRef.current = target;
			currentPosRef.current = target;
			setRenderPos(target);
			return;
		}

		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		const isLargeDistance = distanceMeters(from, target) > snapDistanceMeters;
		const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());

		const animate = (nowTs) => {
			const elapsed = nowTs - start;
			const t = Math.min(Math.max(elapsed / durationMs, 0), 1);
			const eased = isLargeDistance ? easeInOutCubic(t) : t;
			const nextLat = from[0] + (target[0] - from[0]) * eased;
			const nextLng = from[1] + (target[1] - from[1]) * eased;
			const next = [nextLat, nextLng];
			currentPosRef.current = next;
			setRenderPos(next);
			if (t < 1) {
				rafRef.current = requestAnimationFrame(animate);
			} else {
				fromRef.current = target;
				currentPosRef.current = target;
				rafRef.current = null;
			}
		};

		rafRef.current = requestAnimationFrame(animate);

		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		targetLat,
		targetLng,
		durationMs,
		snapDistanceMeters,
	]);

	return renderPos;
};
